import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';

// Layouts
import { AuthLayout } from '@components/layouts/AuthLayout';
import { DashboardLayout } from '@components/layouts/DashboardLayout';

// Auth Pages
import { LoginPage } from '@pages/auth/LoginPage';

// Dashboard
import { DashboardPage } from '@pages/dashboard/DashboardPage';

// Existing pages (kept; will be wired to real APIs incrementally)
import { KYCQueuePage } from '@pages/kyc/KYCQueuePage';
import { KYCDetailPage } from '@pages/kyc/KYCDetailPage';
// Legacy merchant pages: routes redirect to /businesses
// import { MerchantListPage } from '@pages/merchants/MerchantListPage';
// import { MerchantDetailPage } from '@pages/merchants/MerchantDetailPage';
import { AnalyticsPage } from '@pages/analytics/AnalyticsPage';
import { CommissionConfigPage } from '@pages/commission/CommissionConfigPage';
import { CommissionRequestsPage } from '@pages/commission/CommissionRequestsPage';
import { UsersManagementPage } from '@pages/users/UsersManagementPage';
import { AppVersionsPage } from '@pages/ops/AppVersionsPage';
import { NotificationsSendPage } from '@pages/ops/NotificationsSendPage';
import { SettingsPage } from '@pages/settings/SettingsPage';

// New pages (placeholders; will be built out per spec)
import { BusinessesListPage } from '@pages/businesses/BusinessesListPage';
import { BusinessDetailPage } from '@pages/businesses/BusinessDetailPage';
import { OrdersListPage } from '@pages/orders/OrdersListPage';
import { OrderDetailPage } from '@pages/orders/OrderDetailPage';
import { RolesListPage } from '@pages/roles/RolesListPage';
import { DepartmentsListPage } from '@pages/departments/DepartmentsListPage';
import { PermissionsBrowserPage } from '@pages/permissions/PermissionsBrowserPage';
import { CategoriesPage } from '@pages/catalog/CategoriesPage';
import { BrandsPage } from '@pages/catalog/BrandsPage';
import { ProductsReviewPage } from '@pages/products/ProductsReviewPage';
import { RidersListPage } from '@pages/riders/RidersListPage';
import { RiderDetailPage } from '@pages/riders/RiderDetailPage';
import { AuditLogsPage } from '@pages/audit/AuditLogsPage';
import { DisputeQueuePage } from '@pages/disputes/DisputeQueuePage';
import { DisputeDetailPage } from '@pages/disputes/DisputeDetailPage';

// Finance — ledger explorer
import { MoneyDashboardPage } from '@pages/finance/MoneyDashboardPage';
import { OrderSplitPage } from '@pages/finance/OrderSplitPage';
import { WalletsPage } from '@pages/finance/WalletsPage';
import { WalletDetailPage } from '@pages/finance/WalletDetailPage';
import { LedgerAnomaliesPage } from '@pages/finance/LedgerAnomaliesPage';
import { MoneyTracePage } from '@pages/finance/MoneyTracePage';
import { TreasuryPage } from '@pages/finance/TreasuryPage';
import { OwnerLedgerPage } from '@pages/finance/OwnerLedgerPage';
import { WithdrawalsPage } from '@pages/finance/WithdrawalsPage';
import { PaymentClaimsPage } from '@pages/finance/PaymentClaimsPage';
import { DeletionRequestsPage } from '@pages/users/DeletionRequestsPage';
import { ApplicationsPage } from '@pages/applications/ApplicationsPage';

// Delivery
import { DeliveryOverviewPage } from '@pages/delivery/DeliveryOverviewPage';
import { DeliveryIncidentsPage } from '@pages/delivery/DeliveryIncidentsPage';
import { ProvidersListPage } from '@pages/delivery/providers/ProvidersListPage';
import { BoltConfigPage } from '@pages/delivery/providers/bolt/BoltConfigPage';
import { GlovoConfigPage } from '@pages/delivery/providers/glovo/GlovoConfigPage';
import { UberConfigPage } from '@pages/delivery/providers/uber/UberConfigPage';
import { InDriverConfigPage } from '@pages/delivery/providers/indriver/InDriverConfigPage';
import { DemoProviderPage } from '@pages/delivery/providers/demo/DemoProviderPage';
import { DeliverySettingsPage } from '@pages/delivery/settings/DeliverySettingsPage';

// 404
import { NotFoundPage } from '@pages/NotFoundPage';

export const router = createBrowserRouter([
  // Auth Routes
  {
    path: '/login',
    element: <AuthLayout />,
    children: [{ index: true, element: <LoginPage /> }],
  },

  // Protected Dashboard Routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },

      // Users
      { path: 'users', element: <UsersManagementPage /> },
      { path: 'deletion-requests', element: <DeletionRequestsPage /> },
      { path: 'app-versions', element: <AppVersionsPage /> },
      { path: 'notifications', element: <NotificationsSendPage /> },

      // Businesses (replaces old "merchants" route)
      {
        path: 'businesses',
        children: [
          { index: true, element: <BusinessesListPage /> },
          { path: ':businessId', element: <BusinessDetailPage /> },
        ],
      },

      // Departments
      { path: 'departments', element: <DepartmentsListPage /> },

      // Roles
      { path: 'roles', element: <RolesListPage /> },

      // Permissions browser
      { path: 'permissions', element: <PermissionsBrowserPage /> },

      // Catalog
      { path: 'categories', element: <CategoriesPage /> },
      { path: 'brands', element: <BrandsPage /> },
      { path: 'products', element: <ProductsReviewPage /> },

      // KYC
      {
        path: 'kyc',
        children: [
          { index: true, element: <KYCQueuePage /> },
          { path: ':id', element: <KYCDetailPage /> },
        ],
      },

      // Orders
      {
        path: 'orders',
        children: [
          { index: true, element: <OrdersListPage /> },
          { path: ':orderId', element: <OrderDetailPage /> },
        ],
      },

      // Disputes
      {
        path: 'disputes',
        children: [
          { index: true, element: <DisputeQueuePage /> },
          { path: ':disputeId', element: <DisputeDetailPage /> },
        ],
      },

      // Finance — ledger explorer ("where is the money")
      // Registration funnel — who gets to sell or ride
      { path: 'applications', element: <ApplicationsPage /> },

      { path: 'finance', element: <Navigate to="/finance/dashboard" replace /> },
      { path: 'finance/dashboard', element: <MoneyDashboardPage /> },
      { path: 'finance/wallets', element: <WalletsPage /> },
      { path: 'finance/wallets/:walletId', element: <WalletDetailPage /> },
      { path: 'finance/ledger', element: <LedgerAnomaliesPage /> },
      { path: 'finance/trace', element: <MoneyTracePage /> },
      { path: 'finance/trace/:orderId', element: <MoneyTracePage /> },
      // Split drilldown — one order, down to the arithmetic
      { path: 'finance/splits', element: <OrderSplitPage /> },
      { path: 'finance/splits/:orderId', element: <OrderSplitPage /> },
      // Treasury — the operator tools that actually move money
      { path: 'finance/treasury', element: <TreasuryPage /> },
      { path: 'finance/withdrawals', element: <WithdrawalsPage /> },
      // "I paid but it isn't showing" — customer claims awaiting ops review
      { path: 'finance/payment-claims', element: <PaymentClaimsPage /> },
      { path: 'finance/owners/:ownerType/:ownerId', element: <OwnerLedgerPage /> },
      // Still placeholders
      { path: 'finance/payments', element: <DashboardPage /> },
      { path: 'finance/tax', element: <DashboardPage /> },

      // Riders
      {
        path: 'riders',
        children: [
          { index: true, element: <RidersListPage /> },
          { path: ':riderId', element: <RiderDetailPage /> },
        ],
      },

      // Delivery
      {
        path: 'delivery',
        children: [
          { index: true, element: <DeliveryOverviewPage /> },
          { path: 'incidents', element: <DeliveryIncidentsPage /> },
          {
            path: 'providers',
            children: [
              { index: true, element: <ProvidersListPage /> },
              { path: 'bolt',     element: <BoltConfigPage /> },
              { path: 'glovo',    element: <GlovoConfigPage /> },
              { path: 'uber',     element: <UberConfigPage /> },
              { path: 'indriver', element: <InDriverConfigPage /> },
              { path: 'demo',     element: <DemoProviderPage /> },
            ],
          },
          { path: 'settings', element: <DeliverySettingsPage /> },
        ],
      },

      // Commissions
      { path: 'commissions', element: <CommissionConfigPage /> },
      { path: 'commissions/requests', element: <CommissionRequestsPage /> },

      // Settings
      { path: 'settings', element: <SettingsPage /> },

      // Audit logs
      { path: 'audit', element: <AuditLogsPage /> },

      // Analytics (legacy route)
      { path: 'analytics', element: <AnalyticsPage /> },

      // Legacy merchant routes → redirect to businesses
      { path: 'merchants', element: <Navigate to="/businesses" replace /> },
      { path: 'merchants/:id', element: <Navigate to="/businesses" replace /> },
    ],
  },

  // 404
  { path: '*', element: <NotFoundPage /> },
]);
